import { useCallback, useEffect, useRef, useState } from "react";

import { TerminalFrame } from "../components/TerminalFrame";
import { Button, PageBody, PageHeader, cx } from "../components/ui";
import { api } from "../lib/api";
import { useStore } from "../lib/store";

const SECRET_LINE =
  /(password|passwd|api[_-]?key|secret|token|authorization|csrf)(["\s:=]+)([^\s,;"']+)/gi;

function redactLine(line: string): string {
  return line
    .replace(SECRET_LINE, "$1$2***")
    .replace(/Bearer\s+[A-Za-z0-9._\-]+/gi, "Bearer ***");
}

function lineTone(line: string): string {
  const raw = line.toUpperCase();
  if (raw.includes("FAIL") || raw.includes("ERROR") || raw.includes("TRACEBACK")) return "text-fail/80";
  if (raw.includes("WARN")) return "text-live/80";
  if (raw.includes("OK") || raw.includes("REGED") || raw.includes("ACCEPT")) return "text-ok/80";
  return "text-ink/80";
}

function LogPane({ lines, empty }: { lines: string[]; empty: string }) {
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [lines]);
  return (
    <div
      ref={scroller}
      className="scroll-thin min-h-0 flex-1 overflow-auto px-4 py-3 font-mono text-[12px] leading-6"
    >
      {lines.length === 0 ? (
        <div className="text-mute">{empty}</div>
      ) : (
        lines.map((line, index) => (
          <div key={`${index}-${line.slice(0, 24)}`} className={cx("whitespace-pre-wrap break-all", lineTone(line))}>
            {redactLine(line)}
          </div>
        ))
      )}
    </div>
  );
}

/**
 * App log tail + JSONL recent. 404 renders as an empty terminal, never an error wall.
 */
export function Logs() {
  const { run, toast } = useStore();
  const [appLines, setAppLines] = useState<string[]>([]);
  const [jsonlLines, setJsonlLines] = useState<string[]>([]);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    const [app, jsonl] = await Promise.all([api.appLogs(), api.jsonlLogs()]);
    setAppLines(app);
    setJsonlLines(jsonl);
  }, []);

  useEffect(() => {
    void run(load, "Could not load logs");
  }, [load, run, tick]);

  const total = appLines.length + jsonlLines.length;

  return (
    <PageBody className="flex flex-col">
      <PageHeader
        kicker="Engine"
        title="Logs"
        actions={
          <>
            <span className="font-mono text-sm tabular-nums text-teal">{total} lines</span>
            <Button variant="ghost" onClick={() => setTick((n) => n + 1)}>
              Refresh
            </Button>
            <Button variant="ghost" onClick={async () => {
              const text = [...appLines, ...jsonlLines].join("\n");
              try {
                await navigator.clipboard.writeText(text);
                toast(`${text.split("\n").length} lines copied`, "ok");
              } catch {
                toast("Clipboard blocked", "warn");
              }
            }}>Copy</Button>
          </>
        }
      />
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
        <TerminalFrame title="app.log" status={`${appLines.length} lines`} className="min-h-[280px]">
          <LogPane lines={appLines} empty="no app log (empty or 404)" />
        </TerminalFrame>
        <TerminalFrame title="events.jsonl" status={`${jsonlLines.length} lines`} className="min-h-[280px]">
          <LogPane lines={jsonlLines} empty="no jsonl (empty or 404)" />
        </TerminalFrame>
      </div>
    </PageBody>
  );
}
